import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { AvailableLangs, TranslocoService } from '@jsverse/transloco';
import { Subject, take, takeUntil } from 'rxjs';
import { UserPreferencesService } from '@core/preferences/user-preferences.service';

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages',
    standalone: true,
    imports: [MatButtonModule, MatMenuModule, MatIconModule, MatTooltipModule],
})
export class LanguagesComponent implements OnInit, OnDestroy {
    private readonly preferences = inject(UserPreferencesService);
    availableLangs: AvailableLangs;
    activeLang: string;
    private readonly _destroy$ = new Subject<void>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _translocoService: TranslocoService,
        private _dateAdapter: DateAdapter<unknown>
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the available languages from Transloco
        this.availableLangs = this._translocoService.getAvailableLangs();

        // Subscribe to language changes
        this._translocoService.langChanges$
            .pipe(takeUntil(this._destroy$))
            .subscribe((activeLang) => {
                this.activeLang = activeLang;
                this._syncLocale(activeLang);
                this._changeDetectorRef.markForCheck();
            });

    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     *
     * @param lang
     */
    setActiveLang(lang: string): void {
        if (lang === this.activeLang) {
            return;
        }

        void this.preferences.setLanguage(lang);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------


    private _syncLocale(lang: string): void {
        const locale = lang === 'en' ? 'en-US' : 'de-DE';
        if (typeof document !== 'undefined') {
            document.documentElement.lang = lang;
        }
        this._dateAdapter.setLocale(locale);
    }

}
